import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Heart, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';

// Helper to safely extract error message
const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error?.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
    }
    return JSON.stringify(detail);
  }
  return error?.message || 'Error desconocido';
};

export default function OwnerLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, returnUrl, setReturnUrl } = useAuth();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(loginData.email, loginData.password);
      toast.success('¡Bienvenido de vuelta!');
      
      if (returnUrl) {
        const url = returnUrl;
        setReturnUrl(null);
        navigate(url);
      } else {
        navigate('/owner');
      }
    } catch (error) {
      console.error('Owner login error:', error);
      const errorMsg = getErrorMessage(error);
      toast.error(errorMsg || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(
        registerData.email,
        registerData.password,
        registerData.full_name,
        'owner',
        { phone: registerData.phone }
      );
      toast.success('¡Cuenta creada exitosamente!');
      
      if (returnUrl) {
        const url = returnUrl;
        setReturnUrl(null);
        navigate(url);
      } else {
        navigate('/owner');
      }
    } catch (error) {
      console.error('Signup error:', error);
      const errorMsg = getErrorMessage(error);
      toast.error(errorMsg || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 mb-4">
            <Stethoscope className="h-10 w-10 text-blue-600" />
            <span className="font-bold text-2xl text-slate-900">TapPet</span>
          </Link>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Heart className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-slate-900">Portal Dueño</h1>
          </div>
          <p className="text-slate-600 mt-2">Accede al historial de tu mascota</p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="login" data-testid="owner-login-tab">Iniciar Sesión</TabsTrigger>
            <TabsTrigger value="register" data-testid="owner-register-tab">Registrarse</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-lg">
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    placeholder="tu@email.com"
                    required
                    data-testid="owner-login-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Contraseña
                  </label>
                  <Input
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    placeholder="••••••••"
                    required
                    data-testid="owner-login-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="owner-login-submit-button"
                >
                  {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </Button>
              </form>
            </div>
          </TabsContent>

          <TabsContent value="register">
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-lg">
              <form onSubmit={handleRegister} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nombre Completo
                  </label>
                  <Input
                    type="text"
                    value={registerData.full_name}
                    onChange={(e) => setRegisterData({...registerData, full_name: e.target.value})}
                    placeholder="Juan Pérez"
                    required
                    data-testid="owner-register-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Teléfono
                  </label>
                  <Input
                    type="tel"
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                    placeholder="5512345678"
                    required
                    data-testid="owner-register-phone-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                    placeholder="tu@email.com"
                    required
                    data-testid="owner-register-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Contraseña
                  </label>
                  <Input
                    type="password"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="owner-register-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="owner-register-submit-button"
                >
                  {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
                </Button>
              </form>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-slate-600">
            <Link to="/clinic/login" className="text-blue-600 hover:underline">
              ¿Eres de una clínica? Ingresa aquí
            </Link>
          </p>
          <p className="text-sm text-slate-600">
            <Link to="/" className="text-blue-600 hover:underline">
              Volver al inicio
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
